import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GraduationCap, BookOpen, Users, Sparkles,
  ArrowRight, CheckCircle2, Heart,
  ShieldCheck, Globe, Library, Plus,
  LayoutDashboard, School, Brain,
  Calculator, Microscope, Languages,
  Palette, Timer, Mail,
  Crown, TrendingUp, Star, Award, Shield, User
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { createClientComponentClient } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import HardshipApplicationForm from '@/components/HardshipApplicationForm';
import SEO from '@/components/layout/SEO';

// ── Colour tokens (Matching Elite premium feel) ───────────────────────────────
const EMERALD = '#10b981';
const EMERALD_DARK = '#064e3b';
const GOLD = '#C9A84C';
const DARK = '#0A0A0F';
const DARK2 = '#111118';
const DARK3 = '#16161F';
const CARD = '#1A1A24';

const getSubjects = (t: any) => {
  const data = t('homeschool.landing.subjects', { returnObjects: true }) as any[];
  const icons = [
    <Calculator size={24} />, <Microscope size={24} />, <BookOpen size={24} />,
    <Globe size={24} />, <Brain size={24} />, <Palette size={24} />,
    <Brain size={24} />, <LayoutDashboard size={24} />, <Languages size={24} />
  ];
  if (!Array.isArray(data)) return [];
  return data.map((s, i) => ({ ...s, icon: icons[i % icons.length] }));
};

export default function HomeschoolLandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, userProfile } = useAuth();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const supabase = createClientComponentClient();
  const totalCourses = 30; // Elite Courses

  // ── Billing interval type ─────────────────────────────────────────────────────
  type BillingInterval = 'monthly' | '6month' | 'yearly';

  interface PricingData {
    standard: { monthly: number; sixMonthly: number; yearly: number };
    elite: { monthly: number; sixMonthly: number; yearly: number };
    bundle: { monthly: number; sixMonthly: number; yearly: number };
  }

  const [billing, setBilling] = useState<BillingInterval>('monthly');
  const [pricing, setPricing] = useState<PricingData>({
    standard: { monthly: 35, sixMonthly: 189, yearly: 336 },
    elite: { monthly: 45, sixMonthly: 243, yearly: 432 },
    bundle: { monthly: 59, sixMonthly: 318, yearly: 565 },
  });

  useEffect(() => {
    async function fetchPricing() {
      const { data } = await supabase
        .from('subscription_plans')
        .select('plan_key, price_monthly, price_six_monthly, price_yearly')
        .in('plan_key', ['homeschool_single', 'edu_elite', 'edu_bundle']);
      if (data) {
        const p = { ...pricing };
        data.forEach((row: any) => {
          const monthly = Number(row.price_monthly) || 0;
          const sixMonthly = Number(row.price_six_monthly) || 0;
          const yearly = Number(row.price_yearly) || 0;
          if (row.plan_key === 'homeschool_single') p.standard = { monthly, sixMonthly, yearly };
          if (row.plan_key === 'edu_elite') p.elite = { monthly, sixMonthly, yearly };
          if (row.plan_key === 'edu_bundle') p.bundle = { monthly, sixMonthly, yearly };
        });
        setPricing(p);
      }
    }
    fetchPricing();
  }, [supabase]);

  const displayMonthly = (tier: keyof PricingData) => {
    const p = pricing[tier];
    if (billing === 'yearly') return Math.round(p.yearly / 12);
    if (billing === '6month') return Math.round(p.sixMonthly / 6);
    return p.monthly;
  };

  const billedLabel = (tier: keyof PricingData) => {
    const p = pricing[tier];
    if (billing === 'yearly') return `Billed $${p.yearly}/year`;
    if (billing === '6month') return `Billed $${p.sixMonthly}/6 months`;
    return null;
  };

  const steps = [
    { num: '01', title: t('homeschool.landing.howItWorks.steps.0.title'), desc: t('homeschool.landing.howItWorks.steps.0.desc'), icon: <Plus size={24} /> },
    { num: '02', title: t('homeschool.landing.howItWorks.steps.1.title'), desc: t('homeschool.landing.howItWorks.steps.1.desc'), icon: <Library size={24} /> },
    { num: '03', title: t('homeschool.landing.howItWorks.steps.2.title'), desc: t('homeschool.landing.howItWorks.steps.2.desc'), icon: <BookOpen size={24} /> },
    { num: '04', title: t('homeschool.landing.howItWorks.steps.3.title'), desc: t('homeschool.landing.howItWorks.steps.3.desc'), icon: <LayoutDashboard size={24} /> },
  ];

  const faqs = [
    { q: t('homeschool.landing.faq.q1'), a: t('homeschool.landing.faq.a1') },
    { q: t('homeschool.landing.faq.q2'), a: t('homeschool.landing.faq.a2') },
    { q: t('homeschool.landing.faq.q3'), a: t('homeschool.landing.faq.a3') },
    { q: t('homeschool.landing.faq.q4'), a: t('homeschool.landing.faq.a4') },
    { q: t('homeschool.landing.faq.q5'), a: t('homeschool.landing.faq.a5') },
    { q: t('homeschool.landing.faq.q6'), a: t('homeschool.landing.faq.a6') },
  ];

  const enrolPath = (plan: string) => `/homeschool/enrol?plan=${plan}`;
  const individualPath = (plan: string) => `/homeschool/enrol?plan=${plan}&type=individual`;

  const localizedSubjects = getSubjects(t);

  return (
    <div className="min-h-screen text-white font-sans selection:bg-emerald-500/30 overflow-x-hidden" style={{ background: DARK }}>
      <SEO
        title={t('homeschool.landing.headline')}
        description={t('homeschool.landing.subheadline')}
        url="https://stillwatersacademy.com/homeschool"
      />

      {/* NAV */}
      <nav className="sticky top-[80px] z-50 py-4 px-6 md:px-12 flex justify-between items-center backdrop-blur-xl border-b border-white/5 bg-black/40">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20 group-hover:scale-105 transition-transform">
            <span className="text-white font-black text-lg italic">S</span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg tracking-tight leading-none text-white">Sage</span>
            <span className="text-[10px] uppercase tracking-widest font-black text-emerald-400/80">{t('homeschool.landing.badge').split('&')[0].trim()}</span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-6 text-sm font-bold text-white/40">
          <Link to="/church" className="hover:text-emerald-400 transition-colors uppercase tracking-widest text-[10px]">{t('homeschool.nav.churches')}</Link>
          <Link to="/learn" className="hover:text-emerald-400 transition-colors uppercase tracking-widest text-[10px]">{t('homeschool.nav.browseSubjects')}</Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Button
                onClick={() => navigate(userProfile?.facility_role === 'admin' ? '/homeschool/parent' : '/learn')}
                variant="outline"
                className="h-9 px-5 text-[10px] font-black rounded-full border border-white/20 text-white/70 hover:border-emerald-400/60 hover:text-white transition-all bg-white/5 uppercase tracking-widest"
              >
                <User size={12} className="mr-1.5 inline" /> My Dashboard
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/auth/login')}
                variant="outline"
                className="h-9 px-5 text-[10px] font-black rounded-full border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-all bg-transparent uppercase tracking-widest"
              >
                Sign In
              </Button>
            )}
            <Button
              onClick={() => navigate('/homeschool/enrol')}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-black px-6 rounded-full shadow-lg shadow-emerald-500/20 uppercase text-[10px] tracking-widest border-none"
            >
              {t('homeschool.nav.enrolNow')}
            </Button>
          </div>
        </div>

        {/* Mobile Enrol CTA */}
        <div className="flex md:hidden items-center gap-2">
          <Button
            onClick={() => navigate('/homeschool/enrol')}
            className="bg-emerald-600 hover:bg-emerald-500 text-white font-black px-4 py-1.5 rounded-full shadow-md text-[10px] tracking-widest uppercase border-none"
          >
            {t('homeschool.nav.enrolNow', 'Enrol')}
          </Button>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="relative pt-20 pb-32 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full opacity-40 pointer-events-none"
          style={{ background: 'radial-gradient(circle at 50% -20%, #10b98133 0%, transparent 70%)' }} />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-black text-[10px] tracking-[0.3em] uppercase shadow-sm">
              <Sparkles size={12} />
              <span>{t('homeschool.landing.badge')}</span>
            </div>

            <h1 className="text-5xl md:text-8xl font-black text-white leading-[1.0] tracking-tight">
              {t('homeschool.landing.headline')}
            </h1>

            <p className="text-xl text-white/40 max-w-xl leading-relaxed font-medium">
              {t('homeschool.landing.subheadline')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="xl"
                onClick={() => navigate('/homeschool/enrol')}
                className="h-16 px-10 text-lg font-black bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl shadow-xl shadow-emerald-500/20 active:scale-95 transition-all"
              >
                {t('homeschool.landing.cta')} <ArrowRight className="ml-2" />
              </Button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Button
                  size="xl"
                  variant="outline"
                  onClick={() => navigate('/learn')}
                  className="h-16 px-10 text-lg font-black border-white/10 text-white hover:bg-white/5 rounded-2xl transition-all shadow-lg"
                >
                  {t('homeschool.landing.ctaSecondary')}
                </Button>
                <button
                  onClick={() => navigate('/homeschool/enrol?type=individual')}
                  className="text-sm font-bold transition-colors text-center"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', padding: '4px 8px' }}
                >
                  Learning for yourself?{' '}
                  <span style={{ color: '#10b981', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                    Join as an individual →
                  </span>
                </button>
              </div>
            </div>

            <div className="pt-8 flex gap-12 border-t border-white/5 w-fit">
              {[
                { val: '113', label: t('homeschool.landing.stats.subjects') },
                { val: t('currency_symbol', '$') + displayMonthly('standard'), label: t('homeschool.subscription.intervals.monthly') },
                { val: '8-18+', label: t('homeschool.landing.stats.ageRange') }
              ].map((stat, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-4xl font-black text-emerald-500">{stat.val}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-black text-white/20 mt-1">{stat.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Parent Dashboard Preview (Dark Themed) */}
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block relative"
          >
            <div className="bg-black/40 border border-white/10 rounded-[2.5rem] p-4 shadow-2xl relative z-10 backdrop-blur-3xl">
              <div className="bg-white/5 rounded-[2rem] overflow-hidden border border-white/5">
                <div className="bg-emerald-700/80 backdrop-blur-md p-5 flex items-center justify-between text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center border border-white/20">
                      <LayoutDashboard size={20} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-black text-xs uppercase tracking-widest text-white">{t('homeschool.landing.parentDashboardPreviewTitle')}</h4>
                      <p className="text-[9px] uppercase tracking-[0.2em] font-black text-emerald-200/60">{t('homeschool.landing.parentDashboardPreviewSubtitle', { count: 2 })}</p>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-white/10 rounded-full border border-white/20 text-[9px] font-black uppercase tracking-widest">{t('homeschool.landing.parentDashboardPreviewLive')}</div>
                </div>

                <div className="p-6 space-y-4">
                  {[
                    { name: t('homeschool.landing.preview.emma'), age: t('homeschool.landing.preview.age', { age: 11 }), score: 78, color: 'emerald', initial: 'E', emoji: '👧' },
                    { name: t('homeschool.landing.preview.jack'), age: t('homeschool.landing.preview.age', { age: 14 }), score: 64, color: 'emerald', initial: 'J', emoji: '👦' }
                  ].map((child, idx) => (
                    <div key={idx} className="bg-white/5 p-5 rounded-2xl border border-white/5 shadow-sm flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl bg-emerald-500/10 border border-emerald-500/20">{child.emoji}</div>
                      <div className="flex-1">
                        <div className="flex justify-between items-end mb-2">
                          <div>
                            <h5 className="font-black text-white text-sm leading-none uppercase tracking-widest">{child.name}</h5>
                            <span className="text-[9px] font-black text-white/20 uppercase tracking-[0.2em] mt-1 inline-block">
                              {t('homeschool.enrol.childAge')}: {child.age.replace(t('homeschool.landing.preview.age', { age: '' }).trim(), '').trim()}
                            </span>
                          </div>
                          <span className="text-lg font-black text-emerald-400">{child.score}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${child.score}%` }}
                            transition={{ duration: 1, delay: 0.5 + idx * 0.2 }}
                            className="h-full bg-emerald-500 shadow-[0_0_12px_#10b98166]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 text-center">
                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/20">{t('homeschool.landing.parentDashboardPreviewSummary')}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px] -z-10 animate-pulse" />
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 relative overflow-hidden" style={{ background: DARK2 }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight">{t('homeschool.landing.howItWorks.title')}</h2>
            <p className="text-emerald-400/60 font-black uppercase tracking-[0.25em] text-xs">{t('homeschool.landing.howItWorks.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/3 border border-white/5 p-10 rounded-[2.5rem] transition-all duration-500 group relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 p-8 opacity-5 text-4xl font-black italic select-none group-hover:scale-125 transition-transform duration-700">{step.num}</div>
                <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-8 text-emerald-400 border border-emerald-500/20 group-hover:bg-emerald-500 group-hover:text-black transition-all duration-300">
                  {step.icon}
                </div>
                <h3 className="text-xl font-black text-white mb-4 tracking-tight uppercase">{step.title}</h3>
                <p className="text-sm text-white/30 leading-relaxed font-medium">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* THE 113 IMPACT */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-12">
              <div className="space-y-4">
                <span className="text-xs font-black uppercase tracking-[0.3em] text-emerald-400">{t('homeschool.superior_breadth')}</span>
                <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight leading-none">{t('homeschool.standard_foundation_elite_excellence')}</h2>
                <div className="h-1 w-20 bg-emerald-500 rounded-full" />
              </div>

              <div className="space-y-8">
                <div className="flex gap-6">
                  <div className="w-14 h-14 bg-white/5 rounded-2xl flex-shrink-0 flex items-center justify-center border border-white/10">
                    <BookOpen className="text-emerald-400" size={24} />
                  </div>
                  <div>
                    <h4 className="font-black text-lg uppercase tracking-widest text-white mb-2">84 Standard Subjects</h4>
                    <p className="text-white/40 text-sm leading-relaxed">Comprehensive K-12 coverage including Mathematics, Sciences, Literature, and History. Built for full-time homeschooling success.</p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-14 h-14 bg-white/5 rounded-2xl flex-shrink-0 flex items-center justify-center border border-white/10" style={{ borderColor: `${GOLD}30` }}>
                    <Crown className="text-amber-500" size={24} />
                  </div>
                  <div>
                    <h4 className="font-black text-lg uppercase tracking-widest text-amber-500 mb-2">30 Elite Academy Tracks</h4>
                    <p className="text-white/40 text-sm leading-relaxed">Advanced social intelligence, financial engineering, and global diplomacy tracks designed to cultivate the leaders of tomorrow.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Total Subjects', val: '113', color: EMERALD },
                { label: 'Active Learners', val: '15k+', color: '#60a5fa' },
                { label: 'Parent Rating', val: '4.9/5', color: '#facc15' },
                { label: 'Age Range', val: '8-18+', color: '#f87171' }
              ].map((card, i) => (
                <div key={i} className="bg-white/3 border border-white/5 p-8 rounded-[2rem] flex flex-col justify-end min-h-[160px]">
                  <span className="text-4xl font-black text-white mb-1">{card.val}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: card.color }}>{card.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SUBJECTS ACCORDION GRID */}
      <section className="py-24 px-6" style={{ background: DARK3 }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight">{t('homeschool.landing.subjectsTitle')}</h2>
            <p className="text-white/30 font-bold uppercase tracking-widest text-xs">{t('homeschool.landing.subjectsSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {localizedSubjects.map((subject, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -5 }}
                className="bg-white/3 border border-white/5 p-8 rounded-[2.5rem] hover:bg-white/5 transition-all duration-300 group"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-400 group-hover:bg-emerald-500 group-hover:text-black transition-colors">
                    {subject.icon}
                  </div>
                  <span className="px-3 py-1 bg-white/5 rounded-full text-[9px] font-black uppercase tracking-widest text-white/30 border border-white/10 group-hover:border-emerald-500/30">
                    {t('homeschool.child.ageBracket.primary')}
                  </span>
                </div>
                <h3 className="text-xl font-black text-white mb-2 uppercase tracking-tight leading-none">{subject.name}</h3>
                <p className="text-xs text-white/30 font-medium leading-relaxed">{subject.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING SECTION (Premium Style) ────────────────────────────────────────── */}
      <section className="py-32 px-6" style={{ background: DARK }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tighter italic uppercase">{t('homeschool.landing.pricing.title')}</h2>
            <div className="h-1.5 w-24 bg-emerald-600 mx-auto rounded-full mb-10" />

            <div className="flex justify-center mb-12">
              <div className="bg-white/3 border border-white/5 p-1 rounded-2xl flex gap-1">
                {(['monthly', '6month', 'yearly'] as BillingInterval[]).map((int) => (
                  <button
                    key={int}
                    onClick={() => setBilling(int)}
                    className={`px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${billing === int ? 'bg-emerald-600 text-white shadow-lg' : 'text-white/30 hover:bg-white/5'}`}
                  >
                    {t(`homeschool.subscription.intervals.${int}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Standard */}
            <motion.div whileHover={{ y: -10 }} className="bg-white/2 border border-white/5 p-10 rounded-[3rem] flex flex-col backdrop-blur-3xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-5 text-4xl font-black group-hover:opacity-10 transition-opacity"><School size={40} /></div>
              <div className="mb-8">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white/30 px-3 py-1.5 bg-white/5 rounded-full border border-white/5">{t('homeschool.standard_curriculum')}</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-6xl font-black text-white">${displayMonthly('standard')}</span>
                <span className="text-white/20 font-black uppercase text-xs">/mo</span>
              </div>
              {billedLabel('standard') && <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-6">{billedLabel('standard')}</p>}
              <p className="text-white/40 text-sm font-medium mb-10">84 subjects covering the entire academic journey. Perfect foundation.</p>
              <ul className="space-y-4 mb-12 flex-1">
                {['84 Core subjects included', 'Daily progress tracking', 'Full parent dashboard', 'Weekly email insights', 'AI-guided lessons'].map(f => (
                  <li key={f} className="flex items-center gap-3 text-sm font-bold text-white/60">
                    <CheckCircle2 size={16} className="text-emerald-500" /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate(enrolPath('homeschool_single'))} className="w-full h-16 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-black uppercase text-xs tracking-widest border border-white/10">{t('homeschool.choose_standard')}</Button>
            </motion.div>

            {/* Elite */}
            <motion.div whileHover={{ y: -10 }} className="p-1 rounded-[3rem] relative overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${GOLD}44, transparent, ${GOLD}33)` }}>
              <div className="bg-black/90 p-10 h-full rounded-[2.9rem] flex flex-col relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-5 text-4xl font-black group-hover:opacity-10 transition-opacity text-amber-500"><Crown size={40} /></div>
                <div className="mb-8">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-500 px-4 py-1.5 bg-amber-500/10 rounded-full border border-amber-500/30">ELITE ACADEMY</span>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-6xl font-black text-white">${displayMonthly('elite')}</span>
                  <span className="text-white/20 font-black uppercase text-xs">/mo</span>
                </div>
                {billedLabel('elite') && <p className="text-[10px] font-black uppercase tracking-widest text-amber-500/60 mb-6">{billedLabel('elite')}</p>}
                <p className="text-white/40 text-sm font-medium mb-10">30 specialized tracks in social etiquette, finance, leadership, and diplomacy.</p>
                <ul className="space-y-4 mb-12 flex-1">
                  {['30 specialized courses', 'Etiquette & Social Poise', 'Wealth & Finance Mastery', 'Global Diplomacy tracks', 'Public Speaking & Rhetoric'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm font-bold text-amber-500/80">
                      <Star size={16} className="text-amber-500" /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate(enrolPath('edu_elite'))} className="w-full h-16 rounded-2xl font-black uppercase text-xs tracking-widest text-white border-none" style={{ background: `linear-gradient(135deg, #7a5c20, ${GOLD})` }}>{t('homeschool.enrol_elite')}</Button>
              </div>
            </motion.div>

            {/* Bundle */}
            <motion.div whileHover={{ y: -10 }} className="p-1 rounded-[3rem] relative overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${EMERALD}44, transparent, ${EMERALD}33)` }}>
              <div className="bg-black/90 p-10 h-full rounded-[2.9rem] flex flex-col relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-5 text-4xl font-black group-hover:opacity-10 transition-opacity text-emerald-500"><Sparkles size={40} /></div>
                <div className="mb-8 flex justify-between items-center">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-400 px-4 py-1.5 bg-emerald-500/10 rounded-full border border-emerald-500/30">COMPLETE BUNDLE</span>
                  <span className="text-[9px] font-black bg-emerald-600 text-white px-3 py-1 rounded-full uppercase tracking-tighter">{t('homeschool.best_value')}</span>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-6xl font-black text-white">${displayMonthly('bundle')}</span>
                  <span className="text-white/20 font-black uppercase text-xs">/mo</span>
                </div>
                {billedLabel('bundle') && <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-6">{billedLabel('bundle')}</p>}
                <p className="text-white/40 text-sm font-medium mb-10">{t('homeschool.the_absolute_curriculum_all_standard_elite')}</p>
                <ul className="space-y-4 mb-12 flex-1">
                  {['ALL Standard + Elite subjects', 'Complete Standard access', 'Complete Elite access', 'Priority support & feedback', 'Ultimate family education'].map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm font-bold text-emerald-400/80">
                      <ShieldCheck size={16} className="text-emerald-500" /> {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate(enrolPath('edu_bundle'))} className="w-full h-16 rounded-2xl font-black uppercase text-xs tracking-widest text-white border-none" style={{ background: `linear-gradient(135deg, ${EMERALD_DARK}, ${EMERALD})` }}>{t('homeschool.claim_bundle')}</Button>
              </div>
            </motion.div>
          </div>

          <div className="mt-16 text-center">
            <div className="inline-flex flex-col items-center gap-6 p-10 rounded-[2.5rem] bg-white/2 border border-white/5 backdrop-blur-xl">
              <h3 className="text-2xl font-black uppercase tracking-widest text-white italic">{t('homeschool.family_of_or_more')}</h3>
              <p className="text-white/40 font-medium max-w-lg mb-4">Each additional child receives 50% off their monthly subscription automatically. Our Family Hub is built to scale with your household.</p>
                              <Button onClick={() => navigate('/homeschool/enrol')} className="h-14 px-10 font-black bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-lg shadow-emerald-900/40 uppercase text-xs tracking-widest">{t('homeschool.enrol_family_account')}</Button>
                <p className="text-sm text-white/35 font-medium mt-2">
                  💚 Subscribers earn <span className="text-emerald-400 font-bold">30% referral commission</span> — share with other families, earn monthly. <Link to="/affiliates" className="text-emerald-400 underline underline-offset-2">{t('homeschool.learn_more')}</Link>
                </p>
              </div>
            </div>

          <div className="mt-12 text-center">
            <HardshipApplicationForm applicationType="homeschool" />
            <div className="mt-8">
              <Button variant="outline" onClick={() => navigate('/sponsor')} className="h-14 px-8 text-xs font-black uppercase tracking-widest text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10 rounded-2xl">
                💚 Sponsor a child's education →
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLES ─────────────────────────────────────────────────── */}
      <section className="py-24 px-6" style={{ background: DARK2 }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-black text-white text-center mb-16 tracking-tighter italic uppercase">{t('homeschool.the_mastery_comparison')}</h2>
          <div className="rounded-[2.5rem] overflow-hidden border border-white/5 shadow-2xl backdrop-blur-3xl bg-black/40">
            <div className="grid grid-cols-4 bg-white/5">
              <div className="p-8 font-black text-white/20 text-[10px] uppercase tracking-[0.3em]">{t('homeschool.curriculum_pillars')}</div>
              {['Standard', 'Elite', 'Bundle'].map(h => (
                <div key={h} className="p-8 font-black text-center text-[10px] uppercase tracking-widest" style={{ color: h === 'Elite' ? GOLD : (h === 'Bundle' ? EMERALD : 'rgba(255,255,255,0.4)') }}>{h}</div>
              ))}
            </div>
            {[
              ['84 Academic Subjects', '✓', '—', '✓'],
              ['30 Elite Academy Courses', '—', '✓', '✓'],
              ['Age 8–Adult Tiers', '✓', '✓', '✓'],
              ['Leadership & Oratory', '—', '✓', '✓'],
              ['Financial Engineering', '—', '✓', '✓'],
              ['Cultural Poise & Etiquette', '—', '✓', '✓'],
              ['Parent Progress Dashboard', '✓', '✓', '✓'],
              ['AI-Guided 1:1 Teaching', '✓', '✓', '✓'],
            ].map(([feat, ...vals], ri) => (
              <div key={ri} className="grid grid-cols-4 border-t border-white/5 transition-colors hover:bg-white/[0.02]" style={{ background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                <div className="p-6 text-sm text-white/40 font-bold uppercase tracking-widest">{feat}</div>
                {vals.map((v, ci) => (
                  <div key={ci} className="p-6 text-center font-black text-lg"
                    style={{ color: v === '✓' ? (ci === 1 ? GOLD : (ci === 2 ? EMERALD : '#4ADE80')) : 'rgba(255,255,255,0.05)' }}>
                    {v === '✓' ? <CheckCircle2 size={18} className="mx-auto" /> : '—'}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-32 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-20 space-y-4">
          <span className="text-[10px] font-black uppercase tracking-[0.5em] text-emerald-500">{t('homeschool.curriculum_intelligence')}</span>
          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter italic uppercase">{t('homeschool.landing.faq.title')}</h2>
          <div className="h-1.5 w-24 bg-emerald-600 mx-auto rounded-full" />
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              className="rounded-[1.5rem] overflow-hidden border transition-all duration-300"
              style={{ background: CARD, borderColor: openFaq === i ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.05)' }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full p-8 flex items-center justify-between text-left group"
              >
                <span className="text-base font-black text-white group-hover:text-emerald-400 transition-colors pr-8 leading-snug uppercase tracking-wide">{faq.q}</span>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${openFaq === i ? 'bg-emerald-600 text-white rotate-45 shadow-[0_0_20px_#10b98166]' : 'bg-white/5 text-white/20'}`}>
                  <Plus size={18} strokeWidth={3} />
                </div>
              </button>
              <AnimatePresence>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="px-8 pb-8 text-white/30 font-medium leading-relaxed border-t border-white/5 pt-6 mx-4">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-40 px-6 text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(circle at center, #10b98122 0%, transparent 70%)' }} />

        <div className="max-w-5xl mx-auto relative z-10 space-y-12">
          <h2 className="text-6xl md:text-[8rem] font-black text-white tracking-tighter leading-[0.9] italic uppercase">
            {t('homeschool.landing.footerCta.readyHeadline')}<br />
            <span className="text-emerald-500 not-italic">{t('homeschool.landing.footerCta.readySub')}</span>
          </h2>
          <p className="text-xl text-white/40 font-black uppercase tracking-[0.4em] max-w-2xl mx-auto">
            {t('homeschool.landing.footerCta.footerCtaDetail')}
          </p>
          <div className="flex flex-col items-center gap-8">
            <Button
              size="xl"
              onClick={() => navigate('/homeschool/enrol')}
              className="h-20 px-16 text-2xl font-black bg-emerald-600 hover:bg-emerald-500 text-white rounded-[2.5rem] shadow-[0_20px_60px_#10b98133] active:scale-95 transition-all uppercase tracking-widest"
            >
              {t('homeschool.landing.cta')} <ArrowRight className="ml-3" size={28} />
            </Button>
            <div className="flex gap-8 items-center text-[10px] font-black text-white/20 uppercase tracking-[0.3em]">
              <span>{t('homeschool.no_contracts')}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              <span>{t('homeschool.cancel_any_time')}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              <span>{t('homeschool.start_immediately')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER MINI (Dark) */}
      <footer className="py-16 px-6 border-t border-white/5 bg-black/40 flex flex-col md:flex-row justify-between items-center gap-10 max-w-7xl mx-auto rounded-t-[3rem]">
        <div className="flex items-center gap-3 opacity-30 group cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center font-bold text-white text-sm">S</div>
          <span className="text-[10px] font-black tracking-[0.3em] uppercase text-white">{t('homeschool.landing.footerCta.copyright')}</span>
        </div>
        <div className="flex gap-10 text-[10px] font-black uppercase tracking-[0.4em] text-white/20">
          <Link to="/privacy" className="hover:text-emerald-400 transition-colors">{t('church.nav.privacy')}</Link>
          <Link to="/terms" className="hover:text-emerald-400 transition-colors">{t('church.nav.terms')}</Link>
          <Link to="/contact" className="hover:text-emerald-400 transition-colors">{t('church.nav.contact')}</Link>
        </div>
      </footer>
    </div>
  );
}